import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import classnames from 'classnames';
import { PapiTopic } from '@wix/da-papi-types';
import { SessionContext } from '@wix/da-react-context/pkg/SessionContext';
import { useCurrentPromo } from '@wix/da-shared-react/pkg/utils/hooks/useCurrentPromo';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { useFlag } from '@wix/da-react-context/pkg/flags/hooks/useFlag';
import { OverlayContextProvider } from '@wix/da-react-context/pkg/OverlayContext';
import Header from '../../../Header';
import OrderDropdown from '../../_partials/OrderDropdown';
import BasePageLayout from '../../_partials/BasePageLayout';
import DefaultPageContent from '../../_partials/DefaultPageContent';
import SubmitButton from '../../_partials/SubmitButton';
import DreamUpButton from '../../_partials/DreamUpButton';
import TopicFilterSlider from '../../_partials/TopicFilterSlider';

import s from './BrowsePage.scss';

export interface Props {
  pageType?: string;
  feedType?: string;
  hasOrderSubnav?: boolean;
  topicFilter?: string;
  topicFilterTopic?: PapiTopic;
}

const BrowsePage: React.FC<Props> = ({
  pageType,
  feedType,
  hasOrderSubnav,
  topicFilter,
  topicFilterTopic,
}) => {
  const { t } = useTranslation();
  const { isLoggedIn, aiContentFilter } = useContext(SessionContext);
  const isMobile = useContext(MobileContext);
  const isHomePage = pageType === 'home';
  const isMobileHomePage = isMobile && isHomePage;
  const isTopicsPage = topicFilter && isHomePage;

  // disable DD flag on anon and killswitched home page which is also a DD page
  const rfyKillswitch = useFlag('homepage_rfy_killswitch');
  const showDailyDeviationLabel =
    pageType !== 'home' || (isLoggedIn && !rfyKillswitch);

  const currentPromo = useCurrentPromo('self');
  const showCorePromo = currentPromo && !isMobile;
  const isShowingMobileSubmit =
    isMobileHomePage &&
    isLoggedIn &&
    feedType !== 'posts' &&
    !showCorePromo &&
    !topicFilter;

  const isDreamUpHpCta = useFlag('dreamup_hp_cta');
  const isShowingMobileDreamUpCTA =
    isDreamUpHpCta &&
    isMobileHomePage &&
    !isTopicsPage &&
    isLoggedIn &&
    aiContentFilter !== 'suppress';

  // TODO: at this point we probably want to make a separate version of
  //   BrowsePage for the homepage... all this logic is too much for the default page
  const showTopicsRfy = isHomePage && feedType !== 'posts';

  const title = isMobileHomePage
    ? undefined
    : t([`subnavigation.title.${pageType}`, 'subnavigation.title.home']);

  // on Desktop, show Order Dropdown on the right side of header
  // on Topics Page Desktop or Mobile, always show on right side
  const rightSideOrderDropdown = hasOrderSubnav && (!isMobile || isTopicsPage);

  const headerRightSideContent = rightSideOrderDropdown && (
    <OrderDropdown className={s['order-dropdown']} />
  );

  let stickyTopicsTitle = title;
  if (topicFilterTopic) {
    stickyTopicsTitle = isMobile
      ? topicFilterTopic.name
      : t('browsePage.exploreTopic', {
          postProcess: 'reactPostprocessor',
          topic: (
            <span className={s['topics-header-topic']}>
              {topicFilterTopic.name}
            </span>
          ),
        });
  }

  return (
    <OverlayContextProvider value={{ showDailyDeviationLabel }}>
      <BasePageLayout
        title={title}
        headerRightSideContent={headerRightSideContent}
        allowSaleBanner={!isMobile}
        withNavigation={!isHomePage}
        headerClassName={s['header']}
        titleClassName={classnames(isHomePage && s['homepage-title'])}
        {...(showTopicsRfy && {
          contentClassName: s['base-page-content-w-sticky'],
          headerIsStickyOnScrollUp: false,
          withTabHeader: false,
          customHeader: (
            <>
              {isMobile && (
                <TopicFilterSlider
                  className={classnames(
                    s['topics-filter-slider'],
                    !!topicFilterTopic && s['with-topic']
                  )}
                />
              )}
              <Header
                className={s['header']}
                isSticky={false}
                withNavigation={isHomePage ? false : !topicFilter}
                rightSideContent={headerRightSideContent}
                leftSideContent={
                  <span
                    className={classnames(
                      s['title'],
                      !!topicFilterTopic && s['topics-header-title'],
                      !topicFilterTopic && isHomePage && s['homepage-title']
                    )}
                  >
                    {stickyTopicsTitle}
                  </span>
                }
              />
              {!isMobile && (
                <TopicFilterSlider className={s['topics-filter-slider']} />
              )}
            </>
          ),
        })}
      >
        {isMobile && hasOrderSubnav && !rightSideOrderDropdown && (
          <div className={s['order-dropdown']}>
            <OrderDropdown />
          </div>
        )}

        {(isShowingMobileDreamUpCTA || isShowingMobileSubmit) && (
          <div className={s['mobile-buttons-wrapper']}>
            {isShowingMobileSubmit && (
              <SubmitButton className={s['mobile-submit-button']} />
            )}
            {isShowingMobileDreamUpCTA && (
              <DreamUpButton className={s['mobile-dreamup-cta']} />
            )}
          </div>
        )}

        <DefaultPageContent />
      </BasePageLayout>
    </OverlayContextProvider>
  );
};
BrowsePage.displayName = 'BrowsePage';

export default BrowsePage;
